import { useCallback } from 'react';
import {
  type GoodsOption,
  type GoodsOptionsPricingResponse,
} from 'shared/src/api';
import { isNil } from 'shared/src/utils';

import type { ProductOptionMaxDiscountMode } from '../../../hooks/useProductOptionMaxDiscountMode';
import { useGoodsOptionsPricing } from '../queries/useGoodsOptionsPricing';
import { usePostBatchRedeemCoupon } from '../queries/usePostBatchRedeemCoupon';

type AutoRedeemResult = {
  pricing: GoodsOptionsPricingResponse | undefined;
  redeemedCouponSnos: number[];
};

export const useAutoRedeemBeforeCheckout = ({
  selected,
  mode,
}: {
  selected: GoodsOption[];
  mode: ProductOptionMaxDiscountMode;
}) => {
  const { data: pricing, refetch: refetchPricing } = useGoodsOptionsPricing({
    options: selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const { mutateAsync: postBatchRedeemCoupon, isPending: loadingAutoRedeem } =
    usePostBatchRedeemCoupon(selected);

  const autoRedeemBeforeCheckout = useCallback(async (): Promise<AutoRedeemResult> => {
    if (isNil(pricing)) return { pricing: undefined, redeemedCouponSnos: [] };
    if (mode !== 'variantC') return { pricing, redeemedCouponSnos: [] };

    const downloadable = pricing.coupon_list.filter(c => !c.item.is_issued);
    if (downloadable.length === 0) {
      return { pricing, redeemedCouponSnos: [] };
    }

    const redeemedCouponSnos = downloadable.map(c => c.item.sno);
    await postBatchRedeemCoupon();
    const refetched = (await refetchPricing()).data ?? pricing;
    return { pricing: refetched, redeemedCouponSnos };
  }, [mode, postBatchRedeemCoupon, pricing, refetchPricing]);

  return { autoRedeemBeforeCheckout, loadingAutoRedeem };
};
